import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { PreorderPage } from "@/pages/PreorderPage";
import Editor from "@monaco-editor/react";
import "./PreorderPageExample.css";
import {
  createParentMessage,
  isValidIframeMessage,
  isValidParentMessage,
} from "@/types/iframe-messages";

// Theme configurations
const THEMES = {
  light: {
    theme: "light",
  },
  dark: {
    theme: "dark",
  },
  custom: {
    theme: "custom",
    colors: {
      mode: "light",
      common: {
        black: "#000",
        white: "#fff",
      },
      primary: {
        main: "#171818",
      },
      secondary: {
        main: "#9c27b0",
        light: "#ba68c8",
        dark: "#7b1fa2",
        contrastText: "#fff",
      },
      error: {
        main: "#d32f2f",
        light: "#ef5350",
        dark: "#c62828",
        contrastText: "#fff",
      },
      warning: {
        main: "#ed6c02",
        light: "#ff9800",
        dark: "#e65100",
        contrastText: "#fff",
      },
      info: {
        main: "#0288d1",
        light: "#03a9f4",
        dark: "#01579b",
        contrastText: "#fff",
      },
      success: {
        main: "#2e7d32",
        light: "#4caf50",
        dark: "#1b5e20",
        contrastText: "#fff",
      },
      grey: {
        "50": "#fafafa",
        "100": "#f5f5f5",
        "200": "#eeeeee",
        "300": "#e0e0e0",
        "400": "#bdbdbd",
        "500": "#9e9e9e",
        "600": "#757575",
        "700": "#616161",
        "800": "#424242",
        "900": "#212121",
        A100: "#f5f5f5",
        A200: "#eeeeee",
        A400: "#bdbdbd",
        A700: "#616161",
      },
      contrastThreshold: 3,
      tonalOffset: 0.2,
      text: {
        primary: "rgba(0, 0, 0, 0.87)",
        secondary: "rgba(0, 0, 0, 0.6)",
        disabled: "rgba(0, 0, 0, 0.38)",
      },
      divider: "rgba(0, 0, 0, 0.12)",
      background: {
        default: "#E7BC7D",
        paper: "#E7BC7D",
      },
      action: {
        active: "rgba(0, 0, 0, 0.54)",
        hover: "rgba(0, 0, 0, 0.04)",
        hoverOpacity: 0.04,
        selected: "rgba(0, 0, 0, 0.08)",
        selectedOpacity: 0.08,
        disabled: "rgba(0, 0, 0, 0.26)",
        disabledBackground: "rgba(0, 0, 0, 0.12)",
        disabledOpacity: 0.38,
        focus: "rgba(0, 0, 0, 0.12)",
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
      },
    },
  },
} as const;

type ThemeType = keyof typeof THEMES;

interface PostMessageLog {
  id: number;
  timestamp: string;
  event: string;
  data: any;
  origin?: string;
}

/**
 * Example showing how to integrate PreorderPage with React Router and theme switching
 */
type ViewportType = "mobile" | "desktop";

export const PreorderPageExample: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<ThemeType | null>(null);
  const [pendingTheme, setPendingTheme] = useState<ThemeType | null>(null);
  const [messageLogs, setMessageLogs] = useState<PostMessageLog[]>([]);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [currentViewport, setCurrentViewport] =
    useState<ViewportType>("desktop");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const logIdCounter = useRef(0);

  // Test parameters provided by user - extracted as separated variables for demo
  const brandId = "0195f198-a1ac-7236-b08e-250df095c47f";
  const pointId = "0195f198-a2d5-73c2-9d73-ba7fd846a0e8";
  const preorderUrl = `/m/preorder?brandId=${brandId}&pointId=${pointId}`;

  // PostMessage event handler
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Filter out system messages (react-devtools, browser extensions, etc.)
      const isPreorderMessage = isValidIframeMessage(event.data);
      const isThemeMessage = isValidParentMessage(event.data);

      // Only log preorder-related messages
      if (isPreorderMessage || isThemeMessage) {
        const newLog: PostMessageLog = {
          id: ++logIdCounter.current,
          timestamp: new Date().toLocaleTimeString(),
          event: event.data?.type || "unknown",
          data: event.data,
          origin: event.origin,
        };

        setMessageLogs((prev) => [newLog, ...prev].slice(0, 100)); // Keep last 100 logs
      }

      // Handle PostMessage events
      switch (event.data?.type) {
        case "PREORDER_APP_READY":
          if (pendingTheme && iframeRef.current) {
            const theme = THEMES[pendingTheme];
            const themeMessage = createParentMessage("SET_THEME", theme);

            iframeRef.current.contentWindow?.postMessage(themeMessage, "*");
            setCurrentTheme(pendingTheme);
            setPendingTheme(null);
          }
          break;

        case "PREORDER_COMPLETED":
          const orderData = event.data.payload;
          console.log("Заказ создан:", orderData);

          // Close modal and show success message
          handleCloseModal();
          break;

        case "REQUEST_CLOSE":
          handleCloseModal();
          break;

        case "PREORDER_ERROR":
          const errorData = event.data.payload;
          console.error("Ошибка заказа:", errorData);

          // Log error for demonstration
          const errorLog: PostMessageLog = {
            id: ++logIdCounter.current,
            timestamp: new Date().toLocaleTimeString(),
            event: "ORDER_ERROR",
            data: {
              error: errorData,
              message: "Произошла ошибка при создании заказа",
            },
          };
          setMessageLogs((prev) => [errorLog, ...prev].slice(0, 100));
          break;

        default:
          // No action for other event types
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [pendingTheme]);

  const handleOpenModal = (theme?: ThemeType) => {
    setModalOpen(true);
    if (theme) {
      setPendingTheme(theme);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentTheme(null);
    setPendingTheme(null);
    setCurrentViewport("desktop"); // Reset to desktop on close
  };

  const handleViewportChange = (viewport: ViewportType) => {
    setCurrentViewport(viewport);
  };

  const clearLogs = () => {
    setMessageLogs([]);
    setExpandedLogId(null);
    logIdCounter.current = 0;
  };

  const handleLogToggle = (logId: number) => {
    const newExpandedId = expandedLogId === logId ? null : logId;
    setExpandedLogId(newExpandedId);

    if (newExpandedId && logsContainerRef.current) {
      // Delay scroll to allow DOM update
      setTimeout(() => {
        const logElement = document.getElementById(`log-${logId}`);
        if (logElement && logsContainerRef.current) {
          const containerTop = logsContainerRef.current.offsetTop;
          const logTop = logElement.offsetTop;
          logsContainerRef.current.scrollTo({
            top: logTop - containerTop,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  return (
    <div className="preorder-example">
      <div className="container">
        <header className="example-header">
          <h1>Пример интеграции страницы предзаказа</h1>
          <p className="subtitle">
            Демонстрация работы сервиса предзаказа блюд с поддержкой тем
          </p>
        </header>

        <section className="demo-section">
          <h2>Интерактивные примеры с темами</h2>
          <div className="theme-buttons">
            <button
              className="theme-button light-theme"
              onClick={() => handleOpenModal("light")}
            >
              ☀️ Светлая тема
            </button>
            <button
              className="theme-button dark-theme"
              onClick={() => handleOpenModal("dark")}
            >
              🌙 Темная тема
            </button>
            <button
              className="theme-button custom-theme"
              onClick={() => handleOpenModal("custom")}
            >
              🎨 Кастомная тема
            </button>
          </div>
        </section>

        <section className="logs-section">
          <div className="logs-header">
            <h2>PostMessage логи</h2>
            <button className="clear-logs-button" onClick={clearLogs}>
              Очистить логи
            </button>
          </div>
          <div className="logs-container" ref={logsContainerRef}>
            {messageLogs.length === 0 ? (
              <div className="no-logs">
                Логи пусты. Откройте модальное окно для получения событий
                предзаказа.
              </div>
            ) : (
              messageLogs.map((log) => {
                const isExpanded = expandedLogId === log.id;
                return (
                  <div
                    key={log.id}
                    id={`log-${log.id}`}
                    className={`log-entry ${
                      isExpanded ? "expanded" : "collapsed"
                    }`}
                  >
                    <div
                      className="log-header"
                      onClick={() => handleLogToggle(log.id)}
                    >
                      <div className="log-meta">
                        <span className="log-timestamp">{log.timestamp}</span>
                        <span className="log-event">{log.event}</span>
                        {log.origin && (
                          <span className="log-origin">{log.origin}</span>
                        )}
                      </div>
                      <div className="log-toggle-icon">
                        {isExpanded ? "▼" : "▶"}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="log-payload">
                        <pre>{JSON.stringify(log.data, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="info-section">
          <div className="info-card full-width">
            <h3>1. Базовый пример интеграции:</h3>
            <p className="example-description">
              Минимальная интеграция с модальным окном и обработкой событий
              PostMessage
            </p>
            <Editor
              height="600px"
              theme="vs-dark"
              options={{
                readOnly: true,
              }}
              defaultLanguage="javascript"
              defaultValue={`// Параметры для предзаказа
const brandId = "${brandId}";
const pointId = "${pointId}";
const preorderUrl = \`/your-app/preorder?brandId=\${brandId}&pointId=\${pointId}\`;

// Обработка сообщений от iframe
function handleMessage(event) {
  switch (event.data?.type) {
    case 'PREORDER_COMPLETED':
      const orderData = event.data.payload;
      alert(\`Заказ №\${orderData?.orderId} создан!\`);
      closeModal();
      break;
      
    case 'REQUEST_CLOSE':
      closeModal();
      break;
      
    default:
      // No action for other event types
      break;
  }
}

// Открытие в модальном окне
function openModal() {
  const overlay = document.createElement('div');
  overlay.className = 'preorder-modal-overlay';
  overlay.innerHTML = \`
    <div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:1000">
      <iframe src="\${preorderUrl}" style="width:90%;height:90%;border:none;background:white"></iframe>
    </div>
  \`;
  document.body.appendChild(overlay);
  
  overlay.onclick = (e) => e.target === overlay && closeModal();
}

function closeModal() {
  document.querySelector('.preorder-modal-overlay')?.remove();
}

// Инициализация
window.addEventListener('message', handleMessage);`}
            />
          </div>

          <div className="info-card full-width">
            <h3>2. Пример с кастомными темами:</h3>
            <p className="example-description">
              Расширенная интеграция с поддержкой кастомных тем оформления.{" "}
              <a
                href="https://bareynol.github.io/mui-theme-creator/"
                target="_blank"
                rel="noopener noreferrer"
                className="theme-creator-link"
              >
                MUI Theme Creator
              </a>
            </p>
            <Editor
              height="600px"
              theme="vs-dark"
              options={{
                readOnly: true,
              }}
              defaultLanguage="javascript"
              defaultValue={`// Параметры и конфигурация темы
const brandId = "${brandId}";
const pointId = "${pointId}";
const preorderUrl = \`/your-app/preorder?brandId=\${brandId}&pointId=\${pointId}\`;

let currentIframe = null;

// Конфигурация темы
const customTheme = {
  theme: "custom",
  colors: {
    mode: "dark",
    primary: { main: "#4a90e2" },
    secondary: { main: "#7b68ee" },
    background: { default: "#1a1a1a", paper: "#2d2d2d" },
    text: { primary: "#ffffff", secondary: "#b0b0b0" },
    success: { main: "#28a745" },
    error: { main: "#dc3545" }
  }
};

// Обработка сообщений
function handleMessage(event) {
  // Автоматическое применение темы после готовности приложения
  switch (event.data?.type) {
    case 'PREORDER_APP_READY':
      if (currentIframe) {
        const themeMessage = {
          source: 'parent',
          type: 'SET_THEME',
          payload: customTheme,
          timestamp: Date.now()
        };
        currentIframe.contentWindow.postMessage(themeMessage, '*');
      }
      break;
      
    case 'PREORDER_COMPLETED':
      const orderData = event.data.payload;
      alert(\`Заказ №\${orderData?.orderId} создан!\`);
      closeModal();
      break;
      
    case 'REQUEST_CLOSE':
      closeModal();
      break;
      
    default:
      // No action for other event types
      break;
  }
}

// Открытие модального окна
function openModal() {
  const overlay = document.createElement('div');
  overlay.className = 'preorder-modal-overlay';
  overlay.innerHTML = \`
    <div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:1000">
      <iframe src="\${preorderUrl}" style="width:90%;height:90%;border:none;background:white"></iframe>
    </div>
  \`;
  
  currentIframe = overlay.querySelector('iframe');
  document.body.appendChild(overlay);
  overlay.onclick = (e) => e.target === overlay && closeModal();
}

function closeModal() {
  document.querySelector('.preorder-modal-overlay')?.remove();
  currentIframe = null;
}

window.addEventListener('message', handleMessage);

// Использование: openModal() - тема применится автоматически`}
            />
          </div>
        </section>

        {/* Enhanced Modal Implementation with Theme Support */}
        {modalOpen && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div
              className={`modal-content ${
                currentViewport === "mobile"
                  ? "mobile-viewport"
                  : "desktop-viewport"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* <div className="modal-header">
                <h2>
                  Предзаказ блюд
                  {currentTheme && (
                    <span className="theme-indicator">
                      ({THEMES[currentTheme].theme})
                    </span>
                  )}
                  {pendingTheme && !currentTheme && (
                    <span className="theme-indicator pending">
                      (Ожидание {THEMES[pendingTheme].theme})
                    </span>
                  )}
                </h2>
                <button
                  className="close-button"
                  onClick={handleCloseModal}
                  aria-label="Закрыть модальное окно"
                >
                  ✕
                </button>
              </div> */}
              <iframe
                ref={iframeRef}
                src={preorderUrl}
                title="Preorder Page"
                className="modal-iframe"
                allow="payment; geolocation"
              />
              <div className="viewport-switcher">
                <button
                  className={`viewport-button ${
                    currentViewport === "mobile" ? "active" : ""
                  }`}
                  onClick={() => handleViewportChange("mobile")}
                >
                  📱 Mobile
                </button>
                <button
                  className={`viewport-button ${
                    currentViewport === "desktop" ? "active" : ""
                  }`}
                  onClick={() => handleViewportChange("desktop")}
                >
                  🖥️ Desktop
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Full router example with PreorderPage
 */
export const RouterWithPreorderExample: React.FC = () => {
  return (
    <Router>
      <div className="router-example">
        <nav className="navigation">
          <Link to="/" className="nav-link">
            🏠 Главная
          </Link>
          <Link
            to="/preorder?brandId=example-brand&pointId=example-point"
            className="nav-link"
          >
            🍽️ Предзаказ
          </Link>
        </nav>

        <Routes>
          <Route path="/" element={<PreorderPageExample />} />
          <Route path="/preorder" element={<PreorderPage />} />
        </Routes>
      </div>
    </Router>
  );
};
